import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useApi } from './useApi';
import { assertNoError } from './useOptions';

/**
 * Loads a policy list (`GET /<app>/policy/<policyPath>`) when the page opens or the app changes.
 * Loads are silent: no success notification, errors still show.
 * `reload` (after a create or delete) keeps the current table on screen, so its filter, sort and page survive.
 * A change of app clears it, so no row of the previous app can be acted on.
 */
export function usePolicyList(app: string, policyPath: string) {
  const { loading, result, execute } = useApi();
  const base = `/${encodeURIComponent(app)}/policy/${policyPath}`;

  const load = useCallback(
    (keepResult: boolean) => execute(() => api('GET', base), { silent: true, keepResult }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- execute is recreated every render
    [base]
  );
  const reload = useCallback(() => load(true), [load]);

  useEffect(() => {
    if (app) load(false);
  }, [app, load]);

  return { base, loading, result, reload };
}

/** Busy/error state for a modal's confirm button. `submit` resolves true when the call succeeded. */
export function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      assertNoError(await fn());
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, setError, submit };
}
